import type { CompPresetId, TrackState } from '@melodinai/types';
import { useMixerStore } from '../../stores/mixerStore';
import { clsx } from '../../lib/utils';

const PRESETS: { id: CompPresetId; label: string }[] = [
  { id: 'kickTight', label: 'Kick Tight' },
  { id: 'bassControl', label: 'Bass Control' },
  { id: 'vocalPresence', label: 'Vocal Presence' },
  { id: 'tranceGlue', label: 'Trance Glue' },
  { id: 'masterBusGlue', label: 'Master Bus Glue' },
];

interface SslCompressorSectionProps {
  track: TrackState;
}

export function SslCompressorSection({ track }: SslCompressorSectionProps) {
  const setFxParam = useMixerStore((s) => s.setFxParam);
  const applyCompPreset = useMixerStore((s) => s.applyCompPreset);
  const comp = track.fxChain?.find((f) => f.type === 'Compressor');
  const p = (comp?.params || {}) as Record<string, number>;

  const row = (
    key: string,
    label: string,
    min: number,
    max: number,
    step: number,
    unit: string,
    fmt?: (v: number) => string,
  ) => (
    <label className="flex items-center gap-2 text-[9px] text-ssl-muted">
      <span className="w-14">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={p[key] ?? min}
        onChange={(e) => setFxParam(track.id, 'Compressor', key, Number(e.target.value))}
        className="h-1 flex-1 accent-ssl-led"
      />
      <span className="w-14 tabular-nums text-right">
        {fmt ? fmt(p[key] ?? 0) : `${(p[key] ?? 0).toFixed(2)}${unit}`}
      </span>
    </label>
  );

  return (
    <section className="space-y-2 border-b border-ssl-metal/50 pb-3">
      <h3 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ssl-accent">Compressor</h3>
      <div className="flex flex-wrap gap-1">
        {PRESETS.map((pr) => (
          <button
            key={pr.id}
            type="button"
            onClick={() => applyCompPreset(track.id, pr.id)}
            className={clsx(
              'rounded border border-ssl-metal/50 px-1.5 py-0.5 text-[9px] text-ssl-text',
              'hover:border-ssl-accent hover:text-ssl-accent',
            )}
          >
            {pr.label}
          </button>
        ))}
      </div>
      <div className="space-y-1.5 rounded border border-ssl-metal/40 bg-black/20 p-2">
        {row('threshold', 'Thresh', -60, 0, 0.5, ' dB', (v) => v.toFixed(1))}
        {row('ratio', 'Ratio', 1, 20, 0.1, ':1', (v) => v.toFixed(1))}
        {row('attack', 'Attack', 0.001, 0.2, 0.001, ' s', (v) => (v * 1000).toFixed(0) + 'ms')}
        {row('release', 'Release', 0.01, 1, 0.01, ' s', (v) => (v * 1000).toFixed(0) + 'ms')}
        {row('makeup', 'Makeup', 0, 24, 0.1, ' dB', (v) => v.toFixed(1))}
      </div>
    </section>
  );
}
